import { gsap } from 'gsap';

/**
 * Category Testimonials Slider Module
 *
 * Gère les sliders de témoignages sur les pages de catégorie.
 * Basé sur TestimonialsSlider mais adapté pour les pages catégories.
 */

/**
 * Classe pour gérer un slider de témoignages de catégorie
 */
class CategoryTestimonialsSlider {
    constructor(container) {
        this.container = container;

        this.wrapper = container.querySelector('.testimonials-grid');
        this.slides = container.querySelectorAll('.testimonial-card');

        // Les boutons sont dans le même niveau parent que le container
        const sliderParent = container.closest('.flex.items-center') || container.parentElement;
        this.prevBtn = sliderParent.querySelector('.testimonials-slider-prev');
        this.nextBtn = sliderParent.querySelector('.testimonials-slider-next');
        this.pagination = sliderParent.parentElement.querySelector('.testimonials-slider-pagination');

        this.currentIndex = 0;
        this.slidesPerView = this.getSlidesPerView();
        this.totalSlides = this.slides.length;
        this.maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);

        this.init();
    }

    /**
     * Initialise le slider
     */
    init() {
        if (!this.wrapper || this.slides.length === 0) {
            return;
        }
        this.setupSlider();
        this.createPagination();
        this.bindEvents();
        this.updateNavigation();

        // Animation d'entrée pour les slides
        gsap.from(this.slides, {
            duration: 0.6,
            y: 30,
            opacity: 0,
            stagger: 0.1,
            ease: "power2.out"
        });
    }

    /**
     * Configure le slider (positionnement initial)
     */
    setupSlider() {
        // Le wrapper utilise grid, donc pas de style de largeur directe nécessaire
        // mais on s'assure que les slides ont la bonne base pour le calcul de translation
        this.slides.forEach(slide => {
            slide.style.flexShrink = '0';
        });
        this.updateSlidePosition();
    }

    /**
     * Détermine le nombre de slides visibles selon la taille d'écran
     */
    getSlidesPerView() {
        const width = window.innerWidth;
        if (width < 640) return 1;   // Mobile (sm): 1
        if (width < 1024) return 2;  // Tablette (md): 2
        return 3;                    // Desktop (lg+): 3
    }

    /**
     * Crée la pagination
     */
    createPagination() {
        if (!this.pagination) return;

        this.pagination.innerHTML = '';
        const totalPages = this.maxIndex + 1;

        for (let i = 0; i < totalPages; i++) {
            const dot = document.createElement('button');
            dot.className = 'w-3 h-3 bg-gray-300 rounded-full cursor-pointer transition-all duration-300 hover:bg-orange-400';
            dot.dataset.index = i;
            dot.setAttribute('aria-label', `Go to slide ${i + 1}`);

            if (i === this.currentIndex) {
                dot.classList.remove('bg-gray-300', 'w-3');
                dot.classList.add('!bg-orange-500', '!w-6');
            }
            this.pagination.appendChild(dot);
        }
    }

    /**
     * Met à jour la position des slides
     */
    updateSlidePosition() {
        // Utilise la largeur du conteneur pour le calcul
        const containerWidth = this.container.offsetWidth;
        const gap = 32; // gap-8 = 2rem = 32px
        const slideWidth = (containerWidth - (this.slidesPerView - 1) * gap) / this.slidesPerView;
        const translateX = -this.currentIndex * (slideWidth + gap);

        gsap.to(this.wrapper, {
            duration: 0.6,
            x: translateX,
            ease: "power2.out"
        });
    }

    /**
     * Met à jour l'état de la navigation
     */
    updateNavigation() {
        if (this.prevBtn) {
            this.prevBtn.disabled = this.currentIndex === 0;
        }
        if (this.nextBtn) {
            this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
        }

        if (this.pagination) {
            const dots = this.pagination.querySelectorAll('button');
            dots.forEach((dot, index) => {
                if (index === this.currentIndex) {
                    dot.classList.remove('bg-gray-300', 'w-3');
                    dot.classList.add('!bg-orange-500', '!w-6');
                } else {
                    dot.classList.remove('!bg-orange-500', '!w-6');
                    dot.classList.add('bg-gray-300', 'w-3');
                }
            });
        }
    }

    /**
     * Va au slide suivant
     */
    nextSlide() {
        if (this.currentIndex < this.maxIndex) {
            this.currentIndex++;
            this.updateSlidePosition();
            this.updateNavigation();
        }
    }

    /**
     * Va au slide précédent
     */
    prevSlide() {
        if (this.currentIndex > 0) {
            this.currentIndex--;
            this.updateSlidePosition();
            this.updateNavigation();
        }
    }

    /**
     * Va à un slide spécifique
     */
    goToSlide(index) {
        const newIndex = parseInt(index, 10);
        if (newIndex >= 0 && newIndex <= this.maxIndex && newIndex !== this.currentIndex) {
            this.currentIndex = newIndex;
            this.updateSlidePosition();
            this.updateNavigation();
        }
    }

    /**
     * Lie les événements
     */
    bindEvents() {
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.prevSlide());
        }
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.nextSlide());
        }
        if (this.pagination) {
            this.pagination.addEventListener('click', (e) => {
                if (e.target.matches('button[data-index]')) {
                    this.goToSlide(e.target.dataset.index);
                }
            });
        }

        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => this.handleResize(), 250);
        });
    }

    /**
     * Gère le redimensionnement
     */
    handleResize() {
        const newSlidesPerView = this.getSlidesPerView();
        if (newSlidesPerView !== this.slidesPerView) {
            this.slidesPerView = newSlidesPerView;
            this.maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);
            if (this.currentIndex > this.maxIndex) {
                this.currentIndex = this.maxIndex;
            }
            this.createPagination();
            this.updateSlidePosition();
            this.updateNavigation();
        } else {
            // Recalculer la position même si le nombre de slides ne change pas, car la largeur a pu changer
            this.updateSlidePosition();
        }
    }
}

/**
 * Initialise tous les sliders de témoignages de catégorie sur la page
 */
export function initCategoryTestimonialsSliders() {
    const sliders = document.querySelectorAll('[data-category-testimonials-carousel] .testimonials-slider');
    sliders.forEach((slider) => {
        new CategoryTestimonialsSlider(slider);
    });
}
